package kz.dentalcrm.app.screens.treatment.add

import androidx.compose.animation.animateContentSize
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.ChevronDown
import compose.icons.feathericons.ChevronLeft
import compose.icons.feathericons.ChevronUp
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.add_treatment_title
import dentalcrm.composeapp.generated.resources.custom_option_placeholder
import dentalcrm.composeapp.generated.resources.diagnosis
import dentalcrm.composeapp.generated.resources.save
import dentalcrm.composeapp.generated.resources.select_diagnosis
import dentalcrm.composeapp.generated.resources.select_template
import dentalcrm.composeapp.generated.resources.fill_out_card
import dentalcrm.composeapp.generated.resources.treatment_form
import kz.dentalcrm.app.core.ui.UiStateScaffold
import kz.dentalcrm.app.core.ui.components.ContentButton
import kz.dentalcrm.app.core.ui.components.UIButton
import kz.dentalcrm.app.core.ui.components.UIIconBackButton
import kz.dentalcrm.app.core.ui.components.UISelfDropDownTextInput
import kz.dentalcrm.app.core.ui.components.UISelfTextInput
import kz.dentalcrm.app.core.ui.components.tooth.ToothSelector
import kz.dentalcrm.app.navigation.AppNavigator
import kz.dentalcrm.app.screens.protocols.detail.DetailProtocolEvent
import kz.dentalcrm.app.screens.treatment.add.mvi.AddPatientTreatmentEvent
import kz.dentalcrm.app.screens.treatment.add.mvi.AddPatientTreatmentSideEffect
import kz.dentalcrm.app.screens.treatment.add.mvi.AddPatientTreatmentState
import kz.dentalcrm.app.screens.ui.ContentHeader
import kz.dentalcrm.app.theme.DefaultTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun AddPatientTreatmentScreen(
    appointmentId: Int,
    initialTeeth: List<String>
) {
    val viewModel: AddPatientTreatmentViewModel = koinViewModel()
    val state by viewModel.viewState.collectAsState()
    val event: (AddPatientTreatmentEvent) -> Unit = viewModel::setEvent
    val navigator: AppNavigator = koinInject()

    LaunchedEffect(Unit) {
        viewModel.setEvent(AddPatientTreatmentEvent.LoadData(appointmentId, initialTeeth))
    }

    LaunchedEffect("effect_collector") {
        viewModel.effect.collect { effect ->
            when (effect) {
                is AddPatientTreatmentSideEffect.NavigateBack -> navigator.navigateUp()
                is AddPatientTreatmentSideEffect.ShowError -> {
                    // TODO: Show error toast/snackbar
                }
            }
        }
    }

    UiStateScaffold(
        modifier = Modifier.fillMaxSize(),
        isLoading = state.isLoading
    ) {
        Column(
            modifier = Modifier.fillMaxSize()
                .statusBarsPadding()
        ) {
            ContentHeader(
                modifier = Modifier.fillMaxWidth()
                    .padding(horizontal = 12.dp),
                text = stringResource(Res.string.add_treatment_title),
                leadingContent = {
                    UIIconBackButton(
                        onClick = {
                            navigator.navigateUp()
                        }
                    )
                }
            )

            AddPatientTreatmentContent(
                state = state,
                event = event
            )
        }
    }
}

@OptIn(ExperimentalComposeUiApi::class)
@Composable
private fun AddPatientTreatmentContent(
    state: AddPatientTreatmentState,
    event: (AddPatientTreatmentEvent) -> Unit
) {
    var isTemplateDetailsExpanded by remember { mutableStateOf(false) }
    val focusManager = LocalFocusManager.current
    val keyboardController = LocalSoftwareKeyboardController.current

    LazyColumn(
        modifier = Modifier
            .fillMaxSize()
            .padding(horizontal = 16.dp)
            .animateContentSize()
    ) {
        item(key = "tooth_selector") {
            Spacer(modifier = Modifier.height(16.dp))
            ToothSelector(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(350.dp),
                selectedTeeth = state.selectedTeeth,
                onToothClick = { toothId ->
                    // Hide keyboard when interacting with tooth selector (iOS fix)
                    keyboardController?.hide()
                    focusManager.clearFocus()
                    event(AddPatientTreatmentEvent.ToggleTooth(toothId))
                },
                showPrimary = true,
                showPermanent = true,
                selectedColor = DefaultTheme.colors.primary,
                unselectedColor = DefaultTheme.colors.surfaceAlternative,
                strokeColor = DefaultTheme.colors.outline
            )
            Spacer(modifier = Modifier.height(16.dp))
        }

        item(key = "diagnosis_selector") {
            Text(
                text = stringResource(Res.string.diagnosis),
                style = DefaultTheme.typography.bodyMediumMedium,
                color = DefaultTheme.colors.onSurface
            )
            Spacer(modifier = Modifier.height(8.dp))
            UISelfDropDownTextInput(
                modifier = Modifier.fillMaxWidth(),
                label = stringResource(Res.string.select_diagnosis),
                value = state.selectedDiagnosis?.name.orEmpty(),
                items = state.diagnoses,
                selectedItems = state.selectedDiagnosis?.let { listOf(it) } ?: emptyList(),
                itemToString = { it.name.orEmpty() },
                multiSelect = false,
                onItemsSelected = { selected ->
                    selected.firstOrNull()?.let {
                        event(AddPatientTreatmentEvent.SelectDiagnosis(it))
                    }
                }
            )
            Spacer(modifier = Modifier.height(16.dp))
        }

        item(key = "template_selector") {
            Text(
                text = stringResource(Res.string.treatment_form),
                style = DefaultTheme.typography.bodyMediumMedium,
                color = DefaultTheme.colors.onSurface
            )
            Spacer(modifier = Modifier.height(8.dp))
            UISelfDropDownTextInput(
                modifier = Modifier.fillMaxWidth(),
                label = stringResource(Res.string.select_template),
                value = state.selectedTemplate?.name.orEmpty(),
                items = state.templates,
                selectedItems = state.selectedTemplate?.let { listOf(it) } ?: emptyList(),
                itemToString = { it.name.orEmpty() },
                multiSelect = false,
                onItemsSelected = { selected ->
                    selected.firstOrNull()?.let {
                        event(AddPatientTreatmentEvent.SelectTemplate(it))
                    }
                }
            )
            Spacer(modifier = Modifier.height(16.dp))
        }

        if (state.templateDetails.isNotEmpty()) {
            item(key = "fill_out_card_header") {
                ContentButton(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(
                            color = DefaultTheme.colors.surfaceBackground,
                            shape = RoundedCornerShape(16.dp)
                        ),
                    shape = RoundedCornerShape(16.dp),
                    onClick = {
                        keyboardController?.hide()
                        focusManager.clearFocus()
                        isTemplateDetailsExpanded = !isTemplateDetailsExpanded
                    }
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 16.dp, vertical = 12.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = stringResource(Res.string.fill_out_card),
                            style = DefaultTheme.typography.bodyMediumMedium,
                            color = DefaultTheme.colors.onSurface
                        )
                        Icon(
                            imageVector = if (isTemplateDetailsExpanded) FeatherIcons.ChevronUp else FeatherIcons.ChevronDown,
                            contentDescription = null,
                            tint = DefaultTheme.colors.onSurface,
                            modifier = Modifier.size(20.dp)
                        )
                    }
                }
                Spacer(modifier = Modifier.height(8.dp))
            }
        }
        
        if (state.templateDetails.isNotEmpty() && isTemplateDetailsExpanded) {
            items(
                items = state.templateDetails,
                key = { it.id }
            ) { detail ->
                TreatmentDetailSection(
                    modifier = Modifier.animateItem(),
                    detail = detail,
                    selectedOptionIds = state.selectedOptions[detail.id] ?: emptyList(),
                    customValue = state.customValues[detail.id].orEmpty(),
                    onOptionSelected = { optionId ->
                        event(AddPatientTreatmentEvent.SelectOption(detail.id, optionId))
                    },
                    onCustomValueChanged = { value ->
                        event(AddPatientTreatmentEvent.UpdateCustomValue(detail.id, value))
                    }
                )
            }
        }

        item(key = "save_button") {
            Spacer(modifier = Modifier.height(24.dp))
            UIButton(
                modifier = Modifier.fillMaxWidth(),
                text = stringResource(Res.string.save),
                isLoading = state.isSaving,
                enabled = state.enableSave,
                onClick = {
                    keyboardController?.hide()
                    focusManager.clearFocus()
                    event(AddPatientTreatmentEvent.Save)
                }
            )
            Spacer(modifier = Modifier.height(56.dp).navigationBarsPadding())
        }
    }
}

@Composable
private fun TreatmentDetailSection(
    modifier: Modifier,
    detail: kz.dentalcrm.app.data.network.response_model.protocol_templates.TreatmentDetailResponseItem,
    selectedOptionIds: List<Int>,
    customValue: String,
    onOptionSelected: (Int) -> Unit,
    onCustomValueChanged: (String) -> Unit
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(vertical = 8.dp)
    ) {
        Text(
            text = "${detail.id}. ${detail.name}",
            style = DefaultTheme.typography.bodyMediumMedium,
            color = DefaultTheme.colors.onSurface
        )
        Spacer(modifier = Modifier.height(8.dp))

        detail.options.forEach { option ->
            val isSelected = selectedOptionIds.contains(option.id)
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(8.dp))
                    .background(
                        if (isSelected) DefaultTheme.colors.primarySubdued
                        else DefaultTheme.colors.surfaceBackground
                    )
                    .clickable { onOptionSelected(option.id) }
                    .padding(horizontal = 8.dp, vertical = 4.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Start
            ) {
                Checkbox(
                    checked = isSelected,
                    onCheckedChange = { onOptionSelected(option.id) },
                    colors = CheckboxDefaults.colors(
                        checkedColor = DefaultTheme.colors.primary,
                        uncheckedColor = DefaultTheme.colors.outline
                    )
                )
                Text(
                    text = option.value,
                    style = DefaultTheme.typography.bodySmallRegular,
                    color = DefaultTheme.colors.onSurface,
                    modifier = Modifier.weight(1f)
                )
            }
            Spacer(modifier = Modifier.height(4.dp))
        }

        Spacer(modifier = Modifier.height(8.dp))
        UISelfTextInput(
            modifier = Modifier.fillMaxWidth(),
            label = stringResource(Res.string.custom_option_placeholder),
            value = customValue,
            valueChanged = onCustomValueChanged
        )
        Spacer(modifier = Modifier.height(8.dp))
    }
}
